import { useState } from "react";
import { Button } from "../ui/Button";
import { copyText } from "../lib/clipboard";
import { useOpenSettings } from "../settings/useOpenSettings";
import { useTranslation } from "../i18n";

/**
 * Repo settings → "Agents (MCP)": how to point an external agent at THIS repo's
 * architecture. The endpoint is per repo, so the panel can print the exact URL
 * and a config the reader pastes verbatim — only the token is theirs to fill in
 * (it is shown once, at mint time, and never recoverable).
 */
export interface McpSetupPanelProps {
  slug: string;
  repo: string;
}

/** A labelled, monospace, copy-to-clipboard block. */
function CopyBlock({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const { t } = useTranslation("workspace");
  const [copied, setCopied] = useState(false);

  async function copy() {
    await copyText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-200">{label}</span>
        <Button variant="ghost" size="sm" onClick={copy}>
          {copied ? t("settings.mcp.copied") : t("settings.mcp.copy")}
        </Button>
      </div>
      <pre className="overflow-x-auto rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-xs font-mono leading-relaxed text-slate-100">
        {value}
      </pre>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export function McpSetupPanel({ slug, repo }: McpSetupPanelProps) {
  const { t } = useTranslation("workspace");
  const openTokens = useOpenSettings("ws-tokens");

  // Derived from the page's own origin — a Studio reached over a tunnel, a
  // preview port or the public URL must each print the host the reader used.
  const url = `${window.location.origin}/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}/mcp`;
  const serverName = `archlang-${repo}`;
  const placeholder = "archlang_pat_YOUR_TOKEN";

  const jsonConfig = JSON.stringify(
    {
      mcpServers: {
        [serverName]: {
          type: "http",
          url,
          headers: { Authorization: `Bearer ${placeholder}` },
        },
      },
    },
    null,
    2,
  );

  const cliCommand =
    `claude mcp add --transport http ${serverName} ${url} \\\n` +
    `  --header "Authorization: Bearer ${placeholder}"`;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-100">{t("settings.tabs.repoMcp")}</h2>
      <p className="text-sm text-slate-400">{t("settings.mcp.intro")}</p>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-200">{t("settings.mcp.step1Title")}</h3>
        <p className="text-sm text-slate-400">{t("settings.mcp.step1Body", { repo })}</p>
        <Button variant="secondary" size="sm" onClick={openTokens}>
          {t("settings.mcp.step1Action")}
        </Button>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-200">{t("settings.mcp.step2Title")}</h3>
        <CopyBlock
          label={t("settings.mcp.endpointLabel")}
          value={url}
          hint={t("settings.mcp.endpointHint")}
        />
      </section>

      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-200">{t("settings.mcp.step3Title")}</h3>
        <CopyBlock
          label={t("settings.mcp.jsonLabel")}
          value={jsonConfig}
          hint={t("settings.mcp.jsonHint")}
        />
        <CopyBlock
          label={t("settings.mcp.cliLabel")}
          value={cliCommand}
          hint={t("settings.mcp.cliHint")}
        />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-200">{t("settings.mcp.notesTitle")}</h3>
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-400">
          <li>{t("settings.mcp.noteReadOnly")}</li>
          <li>{t("settings.mcp.noteRef")}</li>
          <li>{t("settings.mcp.noteRevoke")}</li>
        </ul>
      </section>
    </div>
  );
}
